
import React from 'react';
import { motion } from 'framer-motion';
import { triggerVerdictSuccess } from '../../effects/Confetti';

interface VerdictModalProps {
    revealedSouvenir: string;
    question: string;
    options?: string[];
    onConfirm: (isCorrect: boolean) => void;
    isActivePlayer: boolean;
}

const VerdictModal: React.FC<VerdictModalProps> = ({ revealedSouvenir, question, options, onConfirm, isActivePlayer }) => {
    const [input, setInput] = React.useState("");
    const [error, setError] = React.useState(false);

    // Normalization helper (Run once or memoize if needed, but simple regex is cheap)
    const normalize = (str: string) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

    const checkAnswer = (answer: string = input) => {
        if (normalize(answer) === normalize(revealedSouvenir)) {
            // Trigger confetti on correct answer
            triggerVerdictSuccess();
            onConfirm(true);
        } else {
            setError(true);
            setTimeout(() => setError(false), 500); // Shake effect reset
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20, rotate: -2 }}
                animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="max-w-xl w-full polaroid-frame"
            >
                <div className="text-center flex flex-col items-center">
                    {/* Photo area (simulated) */}
                    <div className="w-full aspect-video bg-slate-900 mb-6 rounded-sm shadow-inner relative overflow-hidden flex items-center justify-center">
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.8)_100%)] z-10 pointer-events-none" />
                        <h2 className="text-3xl md:text-5xl font-serif italic text-slate-200 z-20 opacity-80 mix-blend-screen filter drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">
                            {/* HIDE ANSWER UNTIL TYPED (or just show blurred ???) */}
                            <span className="blur-[2px]">???</span>
                        </h2>
                    </div>

                    <div className="text-slate-500 font-mono tracking-widest text-[9px] uppercase mb-4 opacity-50">SOUVENIR N°{Math.floor(Math.random() * 1000)}</div>

                    <div className="mb-8 px-4">
                        <p className="polaroid-text text-xl md:text-2xl leading-relaxed text-slate-800 font-medium">
                            {question}
                        </p>
                    </div>

                    {isActivePlayer ? (
                        <div className="flex flex-col gap-5 justify-center items-center w-full px-4">
                            {options && options.length > 0 ? (
                                /* MCQ MODE */
                                <div className="flex flex-col gap-3 w-full">
                                    {options.map((option, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => checkAnswer(option)}
                                            className={`px-6 py-3 rounded-sm font-serif text-lg text-left polaroid-scribble-btn
                                                ${error ? 'border-red-400/50 bg-red-50 text-red-900' : ''}
                                            `}
                                        >
                                            <span className="opacity-40 mr-3 italic">{idx + 1}.</span>
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                /* TEXT INPUT MODE */
                                <div className="w-full relative">
                                    <input
                                        type="text"
                                        autoFocus
                                        value={input}
                                        onChange={(e) => {
                                            setInput(e.target.value);
                                            setError(false);
                                        }}
                                        onKeyDown={(e) => e.key === 'Enter' && checkAnswer(input)}
                                        placeholder="Écrivez ici..."
                                        className={`w-full bg-transparent border-b-2 px-2 py-2 text-center text-2xl polaroid-text outline-none transition-all placeholder:text-slate-300
                                            ${error ? 'border-red-400 text-red-800 animate-shake' : 'border-slate-300 focus:border-slate-800'}
                                        `}
                                    />
                                    <div className="absolute bottom-0 left-0 w-full h-[1px] bg-slate-200 pointer-events-none" />
                                </div>
                            )}

                            <div className="flex gap-4 mt-6">
                                {!options?.length && (
                                    <button
                                        onClick={() => checkAnswer(input)}
                                        className="px-6 py-2 bg-slate-800 hover:bg-black text-white rounded-sm font-serif italic tracking-wide transition-all"
                                    >
                                        Inscrire
                                    </button>
                                )}

                                <button
                                    onClick={() => onConfirm(false)}
                                    className="px-6 py-2 bg-transparent hover:bg-slate-100 text-slate-500 rounded-sm font-serif italic transition-all border border-slate-200"
                                >
                                    Laisser blanc
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-slate-400 font-serif italic mt-8 text-sm opacity-70">
                            {isActivePlayer ? "" : "Quelqu'un tente de se souvenir..."}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default VerdictModal;
